import { Component, inject, OnInit } from '@angular/core';
import {
  BulkActionStore,
  BulkEntityTypes,
  BulkListTypes,
} from '../../../stores/bulk.state';
import { BulkOverviewListComponent } from '../bulk-overview-list/bulk-overview-list.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../../organisms/mask/mask.component';
import { NgIf } from '@angular/common';
import { SharedModule } from 'primeng/api';
import { SidebarModule } from 'primeng/sidebar';
import { ToastModule } from 'primeng/toast';
import { CountBoxComponent } from '../../atoms/count-box/count-box.component';
import { DividerModule } from 'primeng/divider';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { Indicator } from '../../../models/indicator';
import { DataLayer } from '../../../models/data-layer';
import { CategoryStore } from '../../../stores/category/category.state';

@Component({
  selector: 'app-bulk-overview',
  standalone: true,
  imports: [
    BulkOverviewListComponent,
    HeaderComponent,
    MaskComponent,
    NgIf,
    SharedModule,
    SidebarModule,
    ToastModule,
    CountBoxComponent,
    DividerModule,
    AlertComponent,
  ],
  templateUrl: './bulk-overview.component.html',
  styleUrl: './bulk-overview.component.scss',
})
export class BulkOverviewComponent implements OnInit {
  readonly bulkStore = inject(BulkActionStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly layerStore = inject(DataLayerStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);

  itemsInOverview?: any[];
  sidebarHeader = 'Auswahl';
  sidebarIcon = 'fas fa-info-circle';
  labelProperty = 'name';
  itemType?: BulkListTypes;

  ngOnInit(): void {
    const entityType = this.bulkStore.selectedEntityType();
    if (!entityType) return;
    this.beforeConfirmHookByEntityType(entityType);
  }

  /**
   * Displays the details of the provided items and configures the header based on the provided key.
   *
   * @param {any} items - The items to be displayed in the overview.
   * @param {BulkListTypes} key - The key used to determine the header configuration.
   * @return {void} This method does not return a value.
   */
  showDetails(items: any, key: BulkListTypes): void {
    this.itemsInOverview = items;
    this.setHeaderByKey(key);
  }

  /**
   * Sets the sidebar header, icon, label property, and item type based on the provided key.
   *
   * @param {BulkListTypes} key - The key representing the type of data (e.g., indicators, categories, dataLayers, tags, or indicatorData).
   *                              Determines the header text, icon, label property, and item type for the sidebar.
   * @return {void} This method does not return a value.
   */
  setHeaderByKey(key: BulkListTypes): void {
    switch (key) {
      case 'indicators':
        this.sidebarHeader = 'Indikatoren';
        this.sidebarIcon = 'fas fa-ruler-vertical';
        this.labelProperty = 'name';
        this.itemType = key;
        break;
      case 'categories':
        this.sidebarHeader = 'Indikatorenordner';
        this.sidebarIcon = 'fas fa-folder';
        this.labelProperty = 'name';
        this.itemType = key;
        break;
      case 'dataLayers':
        this.sidebarHeader = 'Jahre';
        this.sidebarIcon = 'fas fa-calendar-days';
        this.labelProperty = 'year';
        this.itemType = key;
        break;
      case 'tags':
        this.sidebarHeader = 'Schlagworte';
        this.sidebarIcon = 'fas fa-tags';
        this.labelProperty = 'title';
        this.itemType = key;
        break;
      case 'indicatorData':
        this.sidebarHeader = 'Daten';
        this.sidebarIcon = 'fas fa-database';
        this.labelProperty = '';
        this.itemType = key;
        break;
    }
  }

  /**
   * Closes the overview by resetting the items in the overview to undefined.
   *
   * @return {void} No return value.
   */
  closeOverview(): void {
    this.itemsInOverview = undefined;
  }

  /**
   * Executes a specific pre-confirmation hook based on the provided entity type.
   *
   * @param {BulkEntityTypes} entityType - The type of the entity for which the pre-confirmation hook is to be executed.
   * @return {void} This method does not return a value.
   */
  private beforeConfirmHookByEntityType(entityType: BulkEntityTypes): void {
    switch (entityType) {
      case 'Category':
      case 'Indicator':
        break;
      case 'IndicatorData':
        this.selectData();
    }
  }

  /**
   * Selects data by retrieving and mapping indicators and data layers from the bulk store,
   * then filters data from the data store based on the selected indicators and data layers.
   * Updates the selection in the data store if matching data is found, otherwise clears the selection.
   *
   * @return {void} No return value.
   */
  private selectData(): void {
    const selectedIndicators: string[] = this.bulkStore
      .indicators()
      .map((item: Indicator) => item._id);
    const selectedDataLayers: string[] = this.bulkStore
      .dataLayers()
      .map((item: DataLayer) => item._id);
    const dataToSelect = this.dataStore.getByDataLayersAndIndicators(
      selectedDataLayers,
      selectedIndicators
    );

    if (dataToSelect && dataToSelect.length > 0) {
      this.dataStore.updateSelection(dataToSelect);
    } else {
      this.dataStore.clearSelection();
    }
  }
}
